"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronDown, ChevronRight, Shield } from "lucide-react";
import { useTranslations } from "next-intl";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ADMIN_NAV_LINKS } from "@/lib/admin-nav-links";

type AdminNavDropdownProps = {
  variant?: "nav" | "sidebar";
  onNavigate?: () => void;
};

export function AdminNavDropdown({
  variant = "nav",
  onNavigate,
}: AdminNavDropdownProps) {
  const t = useTranslations("Sidebar");
  const pathname = usePathname();
  const isSidebar = variant === "sidebar";
  const isAdminRoute = pathname.startsWith("/admin");

  return (
    <DropdownMenu modal={isSidebar ? false : undefined}>
      <DropdownMenuTrigger asChild>
        {isSidebar ? (
          <button
            type="button"
            className={`flex w-full cursor-pointer items-center gap-2.5 rounded-lg border border-dashed px-3 py-2.5 text-sm font-medium transition-colors ${
              isAdminRoute
                ? "border-(--mk-gold-border) bg-(--mk-gold-bg) text-(--mk-gold-light)"
                : "border-(--mk-gold-border) text-(--mk-gold-light) hover:bg-(--mk-gold-bg)"
            }`}
          >
            <Shield className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate text-left">
              {t("adminConsole")}
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-(--mk-gold)" />
          </button>
        ) : (
          <button
            type="button"
            className="flex min-h-11 cursor-pointer items-center gap-1 px-1 text-sm font-medium text-(--mk-cream-dim) outline-none transition-colors hover:text-(--mk-cream)"
          >
            {t("admin")}
            <ChevronDown className="h-4 w-4" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={isSidebar ? "start" : "end"}
        side={isSidebar ? "top" : "bottom"}
        sideOffset={isSidebar ? 8 : 4}
        className={
          isSidebar
            ? "w-56 border-(--mk-cream-08) bg-[#1c1914] p-1.5 text-(--mk-cream)"
            : "w-56 border-(--mk-cream-08) bg-(--mk-ink-soft) text-(--mk-cream)"
        }
      >
        {ADMIN_NAV_LINKS.map(({ href, labelKey, icon: Icon }) => (
          <DropdownMenuItem
            key={href}
            asChild
            onSelect={() => onNavigate?.()}
          >
            <Link
              href={href}
              onClick={onNavigate}
              className={
                isSidebar
                  ? "flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm text-(--mk-cream-60) hover:text-(--mk-cream)"
                  : "flex cursor-pointer items-center gap-2 text-(--mk-cream-dim) hover:text-(--mk-cream)"
              }
            >
              <Icon
                className={
                  isSidebar
                    ? "h-4 w-4 shrink-0 text-(--mk-gold)"
                    : "h-4 w-4 text-(--mk-gold)"
                }
              />
              <span>{t(labelKey)}</span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
